/**
 * FaceTemplates — biometric enrollment management for clock-in.
 *
 * Backend exposes ``face_template_url: string | null`` on
 * WorkerResponse, plus POST/DELETE endpoints under
 * ``/v1/organization/workers/{id}/face-template``. Upload goes
 * through ``app.shared.storage`` (local filesystem in dev, S3 in
 * prod via STORAGE_BACKEND).
 *
 * UX:
 * - Per-row [Upload] / [Replace] button triggers a hidden file input;
 *   the chosen image is uploaded as multipart/form-data and the row
 *   is refreshed in place.
 * - Per-row [Delete] confirms then nulls the URL on the worker.
 * - The "Upload" header button is intentionally disabled until the
 *   admin picks a row — bulk upload is a future feature.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  AlertTitle,
  Avatar,
  Link as MuiLink,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import FaceIcon from '@mui/icons-material/Face';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { useTranslation } from 'react-i18next';

import {
  ApiError,
  attendanceApi,
  organizationApi,
  type Schema,
} from '../../../api';
import { ConfirmDialog } from '../../../components/primitives';
import { activeLocale } from '../../../utils/datetime';
import { translateApiError } from '../../../i18n/translateApiError';
import PunchDeviceStatus from './PunchDeviceStatus';

type Worker = Schema<'WorkerResponse'>;
type Department = Schema<'DepartmentResponse'>;
type Location = Schema<'LocationResponse'>;
type Approval = Schema<'ApprovalResponse'>;
type PunchDeviceBinding = Schema<'PunchDeviceBindingResponse'>;

type PunchDeviceLoad =
  | { status: 'loading'; data?: PunchDeviceBinding }
  | { status: 'ready'; data: PunchDeviceBinding }
  | { status: 'error'; data?: undefined };

/** Shape of the liveness summary stored on a face_enrollment approval
 *  (ADR-030 — written by the mobile enroll flow's server-side re-verify). */
interface LivenessInfo {
  passed?: boolean;
  confidence?: number;
  status?: string;
  checked_at?: string;
}

const FaceTemplates: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Worker[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  // `saving` is no longer surfaced in the UI — ConfirmDialog manages
  // its own button-disabled state via its internal loading. Keep the
  // field removed rather than orphaned so ESLint stays happy.
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // BUG-5 fix (2026-07-07): a manager who isn't also an admin (no
  // organization.workers.manage permission) can land here from the
  // face_enrollment "awaiting approval" / "withdrawn" notification and hit
  // a bare 403. Mirrors the FaceReviewApprovals.tsx / mobile face-reviews.tsx
  // posture — same backend RBAC gate, same reusable endpoints, friendlier render.
  const [forbidden, setForbidden] = useState(false);
  const [punchDeviceLoads, setPunchDeviceLoads] = useState<Record<string, PunchDeviceLoad>>({});
  const punchDeviceGenerationRef = useRef(0);
  const punchDeviceLoadedIdsRef = useRef(new Set<string>());
  const punchDeviceInFlightRef = useRef(new Map<string, number>());
  const [resetDeviceFor, setResetDeviceFor] = useState<Worker | null>(null);
  const [resetDeviceReason, setResetDeviceReason] = useState('');

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [toDelete, setToDelete] = useState<Worker | null>(null);

  // Upload state — driven by per-row buttons. The file input is
  // hidden and reused across rows; uploadingFor tracks which worker
  // is mid-upload so we can disable the row's button + show a spinner.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadFor, setUploadFor] = useState<Worker | null>(null);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);
  // ADR-027 — pending face_enrollment requests keyed by worker id. Stores the
  // FULL approval (photo + ADR-030 liveness result) so the review dialog can
  // show the admin what they're approving — not just a bare ✓/✗.
  const [enrollReqByWorker, setEnrollReqByWorker] = useState<Record<string, Approval>>({});
  const [reviewingFor, setReviewingFor] = useState<string | null>(null);
  // Review-detail dialog (the worker whose pending enrollment is open).
  const [reviewWorker, setReviewWorker] = useState<Worker | null>(null);
  const [reviewNote, setReviewNote] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterText, setFilterText] = useState('');
  const [locFilter, setLocFilter] = useState<string>('all');
  const [deptFilter, setDeptFilter] = useState<string>('all');
  /**
   * Enrolment state — the "What" filter this page never had.
   *
   * Without it the only way to find the three people whose photo is waiting was to page
   * through the roster: 10 rows a page means 3 pages at 30 workers and 10 at 100, and the
   * plan tiers say those are the mainstream sizes (Free 10, Starter 30, Professional
   * 100). Clearing the location/department filters does not help — pagination is the
   * thing hiding them, not the filters.
   *
   * It matters more than a convenience because an unapproved enrolment blocks the punch:
   * `attendance.service` refuses one while `face_template_status != 'approved'`.
   */
  const [stateFilter, setStateFilter] = useState<'all' | 'pending' | 'none' | 'approved' | 'rejected'>('all');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setForbidden(false);
    punchDeviceGenerationRef.current += 1;
    punchDeviceLoadedIdsRef.current.clear();
    punchDeviceInFlightRef.current.clear();
    setPunchDeviceLoads({});
    try {
      const [workers, deps, locs] = await Promise.all([
        organizationApi.workersApi.list('active'),
        organizationApi.departmentsApi.list(),
        organizationApi.locationsApi.list(),
      ]);
      setRows(workers);
      setDepartments(deps);
      setLocations(locs);
      setError(null);
      // ADR-027 — best-effort: map pending face_enrollment requests by worker
      // so the per-row Approve/Reject actions know which approval to patch.
      try {
        const pending = await attendanceApi.approvalsApi.list({
          type: 'face_enrollment',
          status: 'pending',
        });
        const m: Record<string, Approval> = {};
        for (const a of pending) {
          if (a.worker?.id) m[a.worker.id] = a;
        }
        setEnrollReqByWorker(m);
      } catch {
        /* non-fatal — approve/reject actions just won't render */
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setForbidden(true);
      } else {
        setError(
          e instanceof ApiError ? translateApiError(e, t) : t('org:faceTemplates.toast.loadFailed'),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const deptName = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of departments) m.set(d.id, d.name);
    return m;
  }, [departments]);

  const summaryStats = useMemo(() => {
    const total = rows.length;
    const registered = rows.filter((e) => !!e.face_template_url).length;
    const missing = total - registered;
    return { total, registered, missing };
  }, [rows]);

  const summaryCards = useMemo(
    () => [
      {
        id: 'total',
        value: summaryStats.total,
        title: t('org:faceTemplates.stats.total'),
        description: t('org:faceTemplates.stats.totalDescription'),
        avatar: {
          icon: <FaceIcon />,
          bgcolor: 'primary.light',
          color: 'primary.main',
        },
      },
      {
        id: 'registered',
        value: summaryStats.registered,
        title: t('org:faceTemplates.stats.registered'),
        description: t('org:faceTemplates.stats.registeredDescription'),
        avatar: {
          icon: <CheckCircleOutlineIcon />,
          bgcolor: 'success.light',
          color: 'success.main',
        },
      },
      {
        id: 'missing',
        value: summaryStats.missing,
        title: t('org:faceTemplates.stats.missing'),
        description: t('org:faceTemplates.stats.missingDescription'),
        avatar: {
          icon: <PersonOffIcon />,
          bgcolor: 'warning.light',
          color: 'warning.dark',
        },
      },
    ],
    [summaryStats, t],
  );

  /**
   * Counted over ALL workers, not over `filtered`.
   *
   * Deliberate: the line's job is to reveal the group you are NOT looking at, so counting
   * the filtered set would make "3 waiting" disappear the moment a location filter hid
   * them — which is the defect it exists to fix.
   */
  const stateCounts = useMemo(() => {
    const c = { pending: 0, none: 0, approved: 0, rejected: 0 } as Record<string, number>;
    for (const r of rows) c[(r.face_template_status ?? 'none') as string] += 1;
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    const scoped = rows.filter((e) => {
      if (locFilter !== 'all' && e.primary_location_id !== locFilter) return false;
      if (deptFilter !== 'all' && e.primary_department_id !== deptFilter) return false;
      // `none` is the default the column carries for somebody who never uploaded, so an
      // absent value reads as "not enrolled" rather than being dropped.
      if (stateFilter !== 'all' && (e.face_template_status ?? 'none') !== stateFilter) {
        return false;
      }
      return true;
    });
    const needle = filterText.trim().toLowerCase();
    if (!needle) return scoped;
    return scoped.filter((e) => {
      const depLabel = (e.primary_department_id &&
        deptName.get(e.primary_department_id)) || '';
      return (
        e.worker_code.toLowerCase().includes(needle) ||
        e.first_name.toLowerCase().includes(needle) ||
        e.last_name.toLowerCase().includes(needle) ||
        depLabel.toLowerCase().includes(needle)
      );
    });
  }, [rows, filterText, locFilter, deptFilter, stateFilter, deptName]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  // Device state is fetched only for the visible page (10 rows by default), not
  // for the full 10-100 worker roster. Loaded/in-flight caches mean typing in the
  // search box can recompute `paginated` without issuing the same requests again.
  // A generation changes only on an explicit roster refresh, so a slower response
  // from the previous generation cannot roll a freshly reset row back.
  useEffect(() => {
    const generation = punchDeviceGenerationRef.current;
    const visibleWorkers = paginated.filter((worker) => (
      !punchDeviceLoadedIdsRef.current.has(worker.id)
      && !punchDeviceInFlightRef.current.has(worker.id)
    ));
    if (visibleWorkers.length === 0) return;

    for (const worker of visibleWorkers) {
      punchDeviceInFlightRef.current.set(worker.id, generation);
    }
    setPunchDeviceLoads((previous) => {
      const next = { ...previous };
      for (const worker of visibleWorkers) {
        const existing = previous[worker.id];
        next[worker.id] = {
          status: 'loading',
          data: existing?.status !== 'error' ? existing?.data : undefined,
        };
      }
      return next;
    });

    void Promise.all(
      visibleWorkers.map(async (worker): Promise<[string, PunchDeviceLoad]> => {
        try {
          const data = await organizationApi.faceTemplatesApi.punchDevice.get(worker.id);
          return [worker.id, { status: 'ready', data }];
        } catch {
          return [worker.id, { status: 'error' }];
        }
      }),
    ).then((results) => {
      for (const [workerId] of results) {
        if (punchDeviceInFlightRef.current.get(workerId) === generation) {
          punchDeviceInFlightRef.current.delete(workerId);
        }
      }
      if (generation !== punchDeviceGenerationRef.current) return;

      setPunchDeviceLoads((previous) => {
        const next = { ...previous };
        for (const [workerId, result] of results) {
          // A successful reset writes `ready` directly. Never let an older load
          // overwrite that newer mutation result.
          if (previous[workerId]?.status !== 'loading') continue;
          next[workerId] = result;
          if (result.status === 'ready') {
            punchDeviceLoadedIdsRef.current.add(workerId);
          }
        }
        return next;
      });
    });
  }, [paginated]);

  // Bulk-remove selection: only REGISTERED rows (you can only remove a template
  // that exists). Select-all toggles the registered rows on the current page.
  // NOTE: these useState hooks MUST be declared before registeredOnPage/
  // allPageSelected below — those reference `selectedIds` inside .every()/.some()
  // closures that run synchronously at render. Declaring the state later put
  // `selectedIds` in the temporal dead zone → "Cannot access 'selectedIds'
  // before initialization" crashed the page once an org had a registered
  // template (registeredOnPage non-empty so the closure actually ran). 2026-06-30.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [openBulkRemove, setOpenBulkRemove] = useState(false);
  const registeredOnPage = paginated.filter((w) => !!w.face_template_url);
  const allPageSelected = registeredOnPage.length > 0 && registeredOnPage.every((w) => selectedIds.includes(w.id));
  const somePageSelected = registeredOnPage.some((w) => selectedIds.includes(w.id));
  const toggleSelectAllPage = () =>
    setSelectedIds((prev) => {
      const ids = registeredOnPage.map((w) => w.id);
      if (allPageSelected) return prev.filter((x) => !ids.includes(x));
      return [...prev, ...ids.filter((x) => !prev.includes(x))];
    });

  // ------------------------------------------------------------------
  // Delete = null out face_template_url
  // ------------------------------------------------------------------

  const handleOpenDeleteDialog = (e: Worker) => {
    setToDelete(e);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setToDelete(null);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await organizationApi.faceTemplatesApi.delete(toDelete.id);
      // Dialog auto-closes on success.
      handleCloseDeleteDialog();
      setToast(
        t('org:faceTemplates.toast.deleted', { name: `${toDelete.first_name} ${toDelete.last_name}` }),
      );
      await fetchAll();
    } catch (e) {
      const message =
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('org:faceTemplates.toast.deleteFailed');
      setToast(message);
      // Rethrow so ConfirmDialog keeps itself open for retry.
      throw e;
    }
  };

  const handleOpenPunchDeviceReset = (worker: Worker) => {
    setResetDeviceFor(worker);
    setResetDeviceReason('');
  };

  const handleClosePunchDeviceReset = () => {
    setResetDeviceFor(null);
    setResetDeviceReason('');
  };

  const handlePunchDeviceReset = async () => {
    if (!resetDeviceFor) return;
    const worker = resetDeviceFor;
    try {
      const reason = resetDeviceReason.trim();
      const data = await organizationApi.faceTemplatesApi.punchDevice.reset(
        worker.id,
        reason ? { reason } : {},
      );
      setPunchDeviceLoads((previous) => ({
        ...previous,
        [worker.id]: { status: 'ready', data },
      }));
      setToast(t('org:faceTemplates.punchDevice.resetDone', {
        name: `${worker.first_name} ${worker.last_name}`,
      }));
    } catch (e) {
      setToast(
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('org:faceTemplates.punchDevice.resetFailed'),
      );
      throw e;
    }
  };

  // ------------------------------------------------------------------
  // ADR-027 — approve / reject a worker's pending face enrollment.
  const handleReviewEnroll = async (
    worker: Worker,
    status: 'approved' | 'rejected',
  ) => {
    const reqId = enrollReqByWorker[worker.id]?.id;
    if (!reqId) {
      setToast(t('org:faceTemplates.toast.noPendingRequest'));
      return;
    }
    setReviewingFor(worker.id);
    try {
      await attendanceApi.approvalsApi.update(reqId, {
        status,
        ...(status === 'rejected' && reviewNote.trim() ? { note: reviewNote.trim() } : {}),
      });
      setToast(
        t(
          status === 'approved'
            ? 'org:faceTemplates.toast.approved'
            : 'org:faceTemplates.toast.rejected',
          { name: `${worker.first_name} ${worker.last_name}` },
        ),
      );
      setReviewWorker(null);
      setReviewNote('');
      await fetchAll();
    } catch (e) {
      setToast(
        e instanceof ApiError ? translateApiError(e, t) : t('org:faceTemplates.toast.reviewFailed'),
      );
    } finally {
      setReviewingFor(null);
    }
  };

  // Upload — open file picker scoped to a worker, then POST multipart
  // ------------------------------------------------------------------

  const handleUploadClick = (worker: Worker) => {
    setUploadFor(worker);
    // Programmatically open the hidden file input. Reset value first
    // so picking the same filename twice still fires onChange.
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  // Issue 4 (Path C 2026-05-22) — admin-triggered prompt asking the
  // worker to self-upload + grant consent via the new
  // /me/face-template page. Posts to invite-self-upload, which writes
  // a Notification + fanout (in-app bell + email + push).
  const [invitingFor, setInvitingFor] = useState<string | null>(null);
  const handleInviteSelfUpload = async (worker: Worker) => {
    if (invitingFor) return;
    setInvitingFor(worker.id);
    try {
      await organizationApi.faceTemplatesApi.inviteSelfUpload(worker.id);
      setToast(
        worker.email
          ? t('org:faceTemplates.toast.inviteSent', { email: worker.email })
          : t('org:faceTemplates.toast.inviteQueued'),
      );
    } catch (e) {
      setToast(
        e instanceof ApiError ? translateApiError(e, t) : t('org:faceTemplates.toast.inviteFailed'),
      );
    } finally {
      setInvitingFor(null);
    }
  };

  // Invite ALL un-enrolled active workers to self-enroll (bulk onboarding).
  // Self-enrollment + liveness (ADR-030) is the model, so this replaces the old
  // admin "bulk photo upload" (which bypassed liveness). Loops the per-worker
  // invite client-side — SMB scale (tens of workers); no bulk endpoint needed.
  const [invitingAll, setInvitingAll] = useState(false);
  const handleInviteAll = async () => {
    if (invitingAll) return;
    const targets = rows.filter((w) => !w.face_template_url);
    if (targets.length === 0) return;
    setInvitingAll(true);
    let ok = 0;
    for (const w of targets) {
      try {
        await organizationApi.faceTemplatesApi.inviteSelfUpload(w.id);
        ok += 1;
      } catch {
        /* skip — continue inviting the rest */
      }
    }
    setInvitingAll(false);
    setToast(t('org:faceTemplates.toast.inviteAllDone', { ok, total: targets.length }));
  };

  // Bulk remove — select registered rows (checkbox) then delete their templates.
  // Self-enroll is the model, so admins mostly INVITE; bulk remove covers
  // offboarding / policy resets. Loops the per-worker delete (SMB scale).
  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const handleBulkRemove = async () => {
    const ids = selectedIds;
    if (ids.length === 0) return;
    let ok = 0;
    for (const id of ids) {
      try {
        await organizationApi.faceTemplatesApi.delete(id);
        ok += 1;
      } catch {
        /* skip — continue removing the rest */
      }
    }
    setOpenBulkRemove(false);
    setSelectedIds([]);
    setToast(t('org:faceTemplates.toast.bulkRemoveDone', { ok, total: ids.length }));
    await fetchAll();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    const worker = uploadFor;
    setUploadFor(null);
    if (!file || !worker) return;

    // Client-side validation. Backend accepts anything storage backend
    // tolerates, but we want to give an immediate, useful error before
    // the round-trip.
    const TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!TYPES.includes(file.type)) {
      setToast(t('org:faceTemplates.toast.fileTypeUnsupported', { type: file.type || 'unknown' }));
      return;
    }
    const MAX_BYTES = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_BYTES) {
      setToast(t('org:faceTemplates.toast.fileTooLarge', { size: (file.size / 1024 / 1024).toFixed(1) }));
      return;
    }

    setUploadingFor(worker.id);
    try {
      await organizationApi.faceTemplatesApi.upload(worker.id, file);
      setToast(t('org:faceTemplates.toast.uploaded', { name: `${worker.first_name} ${worker.last_name}` }));
      await fetchAll();
    } catch (e) {
      const message =
        e instanceof ApiError ? translateApiError(e, t) : t('org:faceTemplates.toast.uploadFailed');
      setToast(message);
    } finally {
      setUploadingFor(null);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) =>
    setPage(newPage);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
    setPage(0);
  };

  // ------------------------------------------------------------------
  // Render guards
  // ------------------------------------------------------------------

  if (loading && rows.length === 0) {
    return (
      <Container
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (forbidden) {
    return (
      <Container sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ width: '100%', p: 5, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <FaceIcon fontSize="medium" sx={{ color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {t('org:faceTemplates.noAccessTitle')}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            {t('org:faceTemplates.noAccessHint')}
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (error && rows.length === 0) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchAll}>
              {t('common:actions.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
      }}
    >
      <Alert
        severity="info"
        sx={{ width: '100%', mb: 2 }}
        icon={<CloudUploadIcon />}
      >
        <AlertTitle>{t('org:faceTemplates.banner.title')}</AlertTitle>
        {t('org:faceTemplates.banner.body')}
        <Box component="span" sx={{ display: 'block', mt: 0.75, fontSize: '0.8rem' }}>
          <strong>{t('org:faceTemplates.banner.gdprPrefix')}</strong>{' '}
          {t('org:faceTemplates.banner.gdprBody')}
        </Box>
      </Alert>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 1.5,
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontSize: '1.125rem' }}
          >
            {t('org:faceTemplates.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('org:faceTemplates.subtitle')}
          </Typography>
          {/* The two numbers that carry an action, and only those two.
              `pending` blocks a punch outright; `none` is somebody who has not uploaded
              yet, which is what this page exists to chase. Approved and rejected are
              reported for completeness but are not links — nothing is waiting on them.

              Clickable because a count with no way to act on it is a nag: at the
              mainstream sizes the plan tiers describe (10 / 30 / 100 workers) three
              pending rows sit somewhere in 1 to 10 pages of roster, and pagination is
              what hides them, so clearing filters would not help. */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            <MuiLink
              component="button"
              type="button"
              underline="hover"
              onClick={() => setStateFilter(stateFilter === 'pending' ? 'all' : 'pending')}
              sx={{ font: 'inherit', color: stateCounts.pending > 0 ? 'warning.main' : 'inherit' }}
            >
              <strong>{stateCounts.pending}</strong> {t('org:faceTemplates.stats.pending')}
            </MuiLink>
            {' · '}
            <MuiLink
              component="button"
              type="button"
              underline="hover"
              onClick={() => setStateFilter(stateFilter === 'none' ? 'all' : 'none')}
              sx={{ font: 'inherit' }}
            >
              <strong>{stateCounts.none}</strong> {t('org:faceTemplates.stats.notEnrolled')}
            </MuiLink>
            {' · '}
            <strong>{stateCounts.approved}</strong> {t('org:faceTemplates.stats.approved')}
            {stateCounts.rejected > 0 && (
              <>
                {' · '}
                <strong>{stateCounts.rejected}</strong> {t('org:faceTemplates.stats.rejected')}
              </>
            )}
            {stateFilter !== 'all' && (
              <>
                {' · '}
                <MuiLink component="button" type="button" underline="hover"
                  onClick={() => setStateFilter('all')} sx={{ font: 'inherit' }}>
                  {t('org:faceTemplates.stats.showAll')}
                </MuiLink>
              </>
            )}
          </Typography>
        </Box>
        <Box>
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton
              size="small"
              onClick={fetchAll}
              sx={{
                mr: 1,
                border: '1px solid rgba(0,0,0,0.2)',
                borderRadius: 1,
                bgcolor: '#ffffff',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {selectedIds.length > 0 ? (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              size="small"
              onClick={() => setOpenBulkRemove(true)}
              sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
            >
              {t('org:faceTemplates.removeSelectedButton', { count: selectedIds.length })}
            </Button>
          ) : (
            <Tooltip title={t('org:faceTemplates.inviteAllTooltip')}>
              <span>
                <Button
                  variant="outlined"
                  startIcon={invitingAll ? <CircularProgress size={14} /> : <MailOutlineIcon />}
                  size="small"
                  disabled={invitingAll || summaryStats.missing === 0}
                  onClick={handleInviteAll}
                  sx={{
                    borderRadius: 1,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    borderColor: 'rgba(0,0,0,0.2)',
                    bgcolor: '#ffffff',
                  }}
                >
                  {t('org:faceTemplates.inviteAllButton', { count: summaryStats.missing })}
                </Button>
              </span>
            </Tooltip>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            hidden
            onChange={handleFileChange}
          />
        </Box>
      </Box>

      {/* Compact stats — 13" laptop friendly */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5, flexWrap: 'wrap' }}>
        {summaryCards.flatMap((card, i) => [
          i > 0 ? <Typography key={`sep-${card.id}`} variant="caption" color="text.disabled">·</Typography> : null,
          <Typography key={card.id} variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {React.cloneElement(card.avatar.icon, { sx: { fontSize: 14, color: card.avatar.color } })}
            <strong>{card.value}</strong> {card.title}
          </Typography>,
        ]).filter(Boolean)}
      </Stack>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          mb: 1,
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('org:faceTemplates.filters.locationLabel')}</InputLabel>
          <Select
            value={locFilter}
            label={t('org:faceTemplates.filters.locationLabel')}
            onChange={(e) => { setLocFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="all">{t('org:faceTemplates.filters.allLocations')}</MenuItem>
            {locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('org:faceTemplates.filters.departmentLabel')}</InputLabel>
          <Select
            value={deptFilter}
            label={t('org:faceTemplates.filters.departmentLabel')}
            onChange={(e) => { setDeptFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="all">{t('org:faceTemplates.filters.allDepartments')}</MenuItem>
            {departments.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </Select>
        </FormControl>
        {/* Enrolment state. Third position, after Where and Who, because that is where
            Status sits in FILTER_DESIGN_CONVENTION — and because the three sibling list
            pages (Worker documents, Attendance requests, Time entries) all have one there
            and this page did not. A missing control on one page of four is read as this
            page being unfinished, whatever the shortcut above it offers.

            Shares `stateFilter` with the clickable counts under the title rather than
            keeping its own copy: two controls over one fact is the drift svdy-rules §10.5b
            describes, and here it would show as a dropdown reading "All" while the list
            was filtered to pending. */}
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>{t('org:faceTemplates.filters.statusLabel')}</InputLabel>
          <Select
            value={stateFilter}
            label={t('org:faceTemplates.filters.statusLabel')}
            onChange={(e) => {
              setStateFilter(e.target.value as typeof stateFilter);
              setPage(0);
            }}
          >
            <MenuItem value="all">{t('org:faceTemplates.filters.allStatuses')}</MenuItem>
            <MenuItem value="pending">{t('org:faceTemplates.status.pending')}</MenuItem>
            <MenuItem value="none">{t('org:faceTemplates.status.none')}</MenuItem>
            <MenuItem value="approved">{t('org:faceTemplates.status.approved')}</MenuItem>
            <MenuItem value="rejected">{t('org:faceTemplates.status.rejected')}</MenuItem>
          </Select>
        </FormControl>
        <TextField
          variant="outlined"
          label={t('org:faceTemplates.searchLabel')}
          value={filterText}
          onChange={handleFilterChange}
          size="small"
          sx={{ width: 320, maxWidth: '100%' }}
        />
        <Box sx={{ flex: 1 }} />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ '.MuiTablePagination-toolbar': { pl: 0, minHeight: 36 }, '.MuiTablePagination-displayedRows': { m: 0 } }}
        />
      </Box>

      {/* Chips §5 */}
      {(() => {
        const chips: { key: string; label: string; onClear: () => void }[] = [];
        if (locFilter !== 'all') {
          const name = locations.find((l) => l.id === locFilter)?.name ?? locFilter;
          chips.push({
            key: 'loc',
            label: t('org:faceTemplates.filters.locationChip', { name }),
            onClear: () => { setLocFilter('all'); setPage(0); },
          });
        }
        if (deptFilter !== 'all') {
          const name = deptName.get(deptFilter) ?? deptFilter;
          chips.push({
            key: 'dept',
            label: t('org:faceTemplates.filters.departmentChip', { name }),
            onClear: () => { setDeptFilter('all'); setPage(0); },
          });
        }
        if (filterText.trim()) {
          chips.push({
            key: 'search',
            label: t('org:faceTemplates.filters.searchChip', { query: filterText.trim() }),
            onClear: () => { setFilterText(''); setPage(0); },
          });
        }
        if (chips.length === 0) return null;
        return (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              gap: 0.75,
              mb: 1.5,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {chips.map((c) => (
              <Chip
                key={c.key}
                label={c.label}
                size="small"
                onDelete={c.onClear}
                variant="outlined"
                color="primary"
                sx={{ borderRadius: 1, height: 24, fontSize: '0.75rem', fontWeight: 500 }}
              />
            ))}
            <Button
              size="small"
              onClick={() => {
                setLocFilter('all');
                setDeptFilter('all');
                setFilterText('');
                setPage(0);
              }}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                color: 'text.secondary',
                minWidth: 0,
                px: 0.5,
                '&:hover': { bgcolor: 'transparent', color: 'text.primary' },
              }}
            >
              {t('org:faceTemplates.filters.clearAll')}
            </Button>
          </Box>
        );
      })()}

      {filtered.length === 0 && !loading ? (
        <Paper sx={{ p: 3, textAlign: 'center', width: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            {filterText || locFilter !== 'all' || deptFilter !== 'all'
              ? t('org:faceTemplates.empty.noMatches')
              : t('org:faceTemplates.empty.noResults')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}
        >
          <Table sx={{ minWidth: 950 }} aria-label={t('org:faceTemplates.tableAria')}>
            <TableHead
              sx={{
                bgcolor: 'grey.100',
                '& .MuiTableCell-root': { fontWeight: 600 },
              }}
            >
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allPageSelected}
                    indeterminate={somePageSelected && !allPageSelected}
                    onChange={toggleSelectAllPage}
                    disabled={registeredOnPage.length === 0}
                    inputProps={{ 'aria-label': t('org:faceTemplates.table.selectAll', { defaultValue: 'Select all' }) }}
                  />
                </TableCell>
                <TableCell>{t('org:faceTemplates.table.code')}</TableCell>
                <TableCell>{t('org:faceTemplates.table.name')}</TableCell>
                <TableCell>{t('org:faceTemplates.table.department')}</TableCell>
                <TableCell>{t('org:faceTemplates.table.status')}</TableCell>
                <TableCell>{t('org:faceTemplates.table.consent')}</TableCell>
                <TableCell>{t('org:faceTemplates.punchDevice.column')}</TableCell>
                <TableCell align="center">{t('org:faceTemplates.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((worker) => {
                const registered = !!worker.face_template_url;
                const punchDeviceLoad = punchDeviceLoads[worker.id];
                return (
                  <TableRow hover tabIndex={-1} key={worker.id} selected={selectedIds.includes(worker.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedIds.includes(worker.id)}
                        disabled={!registered}
                        onChange={() => toggleSelect(worker.id)}
                        inputProps={{ 'aria-label': `${worker.first_name} ${worker.last_name}` }}
                      />
                    </TableCell>
                    <TableCell>{worker.worker_code}</TableCell>
                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                      >
                        <Avatar
                          src={worker.face_template_url || worker.avatar_url || undefined}
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: registered ? 'success.light' : 'grey.300',
                            color: registered
                              ? 'success.dark'
                              : 'text.secondary',
                          }}
                        >
                          <FaceIcon fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500 }}
                          >
                            {worker.first_name} {worker.last_name}
                          </Typography>
                          {worker.email && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {worker.email}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {worker.primary_department_id
                        ? deptName.get(worker.primary_department_id) ?? '—'
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {registered ? (
                        <Chip
                          label={t('org:faceTemplates.table.registered')}
                          color="success"
                          size="small"
                          icon={<CheckCircleOutlineIcon />}
                        />
                      ) : (
                        <Chip
                          label={t('org:faceTemplates.table.notRegistered')}
                          size="small"
                          variant="outlined"
                          icon={<HighlightOffIcon />}
                        />
                      )}
                      {worker.face_template_status === 'pending' && (
                        <Chip label={t('org:faceTemplates.table.pendingApproval')} color="warning" size="small" sx={{ ml: 0.5 }} />
                      )}
                      {worker.face_template_status === 'approved' && (
                        <Chip label={t('org:faceTemplates.table.approved')} color="success" size="small" variant="outlined" sx={{ ml: 0.5 }} />
                      )}
                      {worker.face_template_status === 'rejected' && (
                        <Chip label={t('org:faceTemplates.table.rejected')} color="error" size="small" variant="outlined" sx={{ ml: 0.5 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {/* Privacy audit Wave 2 — surface GDPR Art. 9 consent
                          state to admins so they know whether face matching
                          will actually run for this worker after upload. */}
                      {worker.face_template_consented_at ? (
                        <Tooltip
                          title={t('org:faceTemplates.tooltips.consentedSince', { date: new Date(worker.face_template_consented_at).toLocaleDateString(activeLocale()) })}
                        >
                          <Chip
                            label={t('org:faceTemplates.table.consented')}
                            color="success"
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title={t('org:faceTemplates.tooltips.noConsentBody')}>
                          <Chip
                            label={t('org:faceTemplates.table.noConsent')}
                            color="warning"
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <PunchDeviceStatus
                        state={punchDeviceLoad?.data}
                        loading={!punchDeviceLoad || (
                          punchDeviceLoad.status === 'loading' && !punchDeviceLoad.data
                        )}
                        error={punchDeviceLoad?.status === 'error'}
                        onReset={() => handleOpenPunchDeviceReset(worker)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {!registered && (
                        <Tooltip title={t('org:faceTemplates.tooltips.inviteSelf')}>
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={invitingFor === worker.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleInviteSelfUpload(worker);
                              }}
                              sx={{ mr: 0.5 }}
                            >
                              {invitingFor === worker.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <MailOutlineIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {worker.face_template_status === 'pending' && (
                        <Tooltip title={t('org:faceTemplates.tooltips.review')}>
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={reviewingFor === worker.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                setReviewNote('');
                                setReviewWorker(worker);
                              }}
                              sx={{ mr: 0.5 }}
                            >
                              {reviewingFor === worker.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <VisibilityOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip
                        title={
                          registered
                            ? t('org:faceTemplates.tooltips.replaceTemplate')
                            : t('org:faceTemplates.tooltips.uploadTemplate')
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={uploadingFor === worker.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUploadClick(worker);
                            }}
                            sx={{ mr: 0.5 }}
                          >
                            {uploadingFor === worker.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <CloudUploadIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          registered
                            ? t('org:faceTemplates.tooltips.removeTemplate')
                            : t('org:faceTemplates.tooltips.noTemplateToRemove')
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={!registered}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDeleteDialog(worker);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ADR-027/030 — review-detail dialog: the admin SEES the submitted photo
          + the liveness result before deciding (not a bare ✓/✗). */}
      <Dialog
        open={!!reviewWorker}
        onClose={() => { setReviewWorker(null); setReviewNote(''); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t('org:faceTemplates.review.title')}
        </DialogTitle>
        <DialogContent dividers>
          {(() => {
            const appr = reviewWorker ? enrollReqByWorker[reviewWorker.id] : undefined;
            const data = (appr?.data ?? {}) as { photo_url?: string; liveness?: LivenessInfo };
            const live = data.liveness;
            return (
              <Stack spacing={2} alignItems="center">
                <Box
                  component="img"
                  src={data.photo_url || undefined}
                  alt={t('org:faceTemplates.review.photoAlt')}
                  sx={{
                    width: 200, height: 200, objectFit: 'cover',
                    borderRadius: 2, border: '1px solid', borderColor: 'divider',
                    bgcolor: 'grey.100',
                  }}
                />
                {reviewWorker && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {reviewWorker.first_name} {reviewWorker.last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {reviewWorker.worker_code}
                    </Typography>
                  </Box>
                )}
                {/* ADR-030 — liveness result the enrollment passed server-side. */}
                {live?.passed ? (
                  <Chip
                    icon={<VerifiedUserOutlinedIcon />}
                    color="success"
                    variant="outlined"
                    size="small"
                    label={t('org:faceTemplates.review.livenessPassed', {
                      confidence: Math.round(live.confidence ?? 0),
                    })}
                  />
                ) : (
                  <Chip
                    color="default"
                    variant="outlined"
                    size="small"
                    label={t('org:faceTemplates.review.livenessNone')}
                  />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                  {t('org:faceTemplates.review.confirmHint')}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label={t('org:faceTemplates.review.rejectNoteLabel')}
                  placeholder={t('org:faceTemplates.review.rejectNotePlaceholder')}
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                  multiline
                  minRows={2}
                />
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ py: 2, justifyContent: 'space-between' }}>
          <Button
            color="error"
            onClick={() => reviewWorker && handleReviewEnroll(reviewWorker, 'rejected')}
            disabled={!!reviewingFor}
          >
            {t('org:faceTemplates.review.reject')}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleOutlineIcon />}
            onClick={() => reviewWorker && handleReviewEnroll(reviewWorker, 'approved')}
            disabled={!!reviewingFor}
          >
            {reviewingFor ? <CircularProgress size={20} /> : t('org:faceTemplates.review.approve')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={openDeleteDialog}
        tone="danger"
        title={t('org:faceTemplates.deleteTitle')}
        description={t('org:faceTemplates.deleteDescription')}
        subject={
          toDelete && (
            <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {toDelete.first_name} {toDelete.last_name}
            </Box>
          )
        }
        confirmLabel={t('org:faceTemplates.removeButton')}
        confirmLoadingLabel={t('org:faceTemplates.removeLoading')}
        onConfirm={handleDelete}
        onClose={handleCloseDeleteDialog}
      />

      <ConfirmDialog
        open={openBulkRemove}
        tone="danger"
        title={t('org:faceTemplates.bulkRemoveTitle')}
        description={t('org:faceTemplates.bulkRemoveDescription', { count: selectedIds.length })}
        confirmLabel={t('org:faceTemplates.removeSelectedButton', { count: selectedIds.length })}
        confirmLoadingLabel={t('org:faceTemplates.removeLoading')}
        onConfirm={handleBulkRemove}
        onClose={() => setOpenBulkRemove(false)}
      />

      <ConfirmDialog
        open={!!resetDeviceFor}
        tone="warning"
        title={t('org:faceTemplates.punchDevice.resetTitle')}
        description={t('org:faceTemplates.punchDevice.resetDescription')}
        subject={
          resetDeviceFor && (
            <Stack spacing={1.5}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                {resetDeviceFor.first_name} {resetDeviceFor.last_name}
              </Typography>
              <TextField
                fullWidth
                size="small"
                label={t('org:faceTemplates.punchDevice.reasonLabel')}
                placeholder={t('org:faceTemplates.punchDevice.reasonPlaceholder')}
                value={resetDeviceReason}
                onChange={(e) => setResetDeviceReason(e.target.value)}
                inputProps={{ maxLength: 500 }}
              />
            </Stack>
          )
        }
        details={t('org:faceTemplates.punchDevice.resetDetails')}
        confirmLabel={t('org:faceTemplates.punchDevice.confirm')}
        confirmLoadingLabel={t('org:faceTemplates.punchDevice.resetting')}
        onConfirm={handlePunchDeviceReset}
        onClose={handleClosePunchDeviceReset}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
        >
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FaceTemplates;
